import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  EffectRef,
  inject,
  input,
  InputSignal,
  ResourceRef,
  signal,
  Signal,
  TemplateRef,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { StatusCodes } from 'http-status-codes';
import { DateTime } from 'luxon';
import { AlertComponent, ExpanderComponent, PageHeaderComponent, SearchInputComponent } from '@libs/common/components';
import {
  OrderHistoryCartItemModel,
  OrderHistoryItemAvailabilityType,
  OrderHistoryItemModel,
  OrderHistoryItemSortByValueType,
  OrderHistoryRecordFilter,
  OrderHistoryRecordModel,
} from '@libs/services/models';
import { CapabilityService } from '@libs/shared/services';
import { AppErrorModel } from '@libs/shared/models';
import { CategoryNodeService, ViewportService } from '@libs/shared/services';
import { CartTrackingService, PartialProductImpressionDirective, ProductImpressionDirective, ProductTrackingListDirective } from '@libs/shared/tracking';
import { OrderSelectionBarComponent } from '@libs/shop/components';
import { TopLevelCategoryNode, TRACKING_AVAILABILITY } from '@libs/shop/models';
import { OrderHistoryDetailsComponent } from '../order-history-details';
import { OrderHistoryItemComponent } from '../order-history-item';
import { OrderHistoryService } from '../services';
import { OrderHistoryRecordFilterComponent } from './order-history-record-filter';
import { OrderHistoryRecordSkeletonComponent } from './order-history-record-skeleton';

@Component({
  selector: 'ui-order-history-record',
  templateUrl: './order-history-record.component.html',
  styleUrls: ['./order-history-record.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [OrderHistoryService],
  imports: [
    AlertComponent,
    ExpanderComponent,
    MatCard,
    OrderHistoryDetailsComponent,
    OrderHistoryItemComponent,
    OrderHistoryRecordSkeletonComponent,
    PageHeaderComponent,
    ProductImpressionDirective,
    PartialProductImpressionDirective,
    ProductTrackingListDirective,
    MatCardContent,
    MatCheckbox,
    OrderSelectionBarComponent,
    OrderHistoryRecordFilterComponent,
    SearchInputComponent,
  ],
})
export class OrderHistoryRecordComponent {
  public orderId: InputSignal<string> = input.required();

  public orderResource: ResourceRef<OrderHistoryRecordModel | undefined> = rxResource({
    params: () => this.orderId(),
    stream: ({ params: orderId }) => this.orderHistoryService.getOrderHistoryRecord(orderId),
  });

  public order: Signal<OrderHistoryRecordModel | undefined> = computed(
    () => this.orderResource.hasValue() ? this.orderResource.value() : undefined,
  );

  // The BFF answers an unknown order number with 404. That is a final answer, not a hiccup the customer
  // could retry away, so it has to reach the "not found" branch instead of the generic error alert.
  public isNotFound: Signal<boolean> = computed(() => {
    const error: AppErrorModel | undefined = this.orderResource.error() as AppErrorModel | undefined;

    return error ? error.status === Number(StatusCodes.NOT_FOUND) : !this.orderResource.hasValue();
  });

  public isAddToCartLoading: Signal<boolean> = computed(() => this.orderHistoryService.isAddingToCart());

  // Reordering pushes the order into the shop cart, which apps running the new app config do not have
  public canReorder: Signal<boolean> = inject(CapabilityService).isAvailable('shop.orderReorder');

  public nodes: Signal<TopLevelCategoryNode<OrderHistoryItemModel>[]> = computed(() => {
    const order: OrderHistoryRecordModel | undefined = this.order();

    return order ? this.categoryNodeService.createCategoryNodes<OrderHistoryItemModel>(order.items) : [];
  });

  public searchTerm: WritableSignal<string> = signal('');
  public filter: WritableSignal<OrderHistoryRecordFilter> = signal({ categoryIds: [] });
  public sort: WritableSignal<OrderHistoryItemSortByValueType> = signal('position');

  public visibleNodes: Signal<TopLevelCategoryNode<OrderHistoryItemModel>[]> = computed(() => {
    const filter: OrderHistoryRecordFilter = this.filter();
    const search: string = this.searchTerm().trim().toLowerCase();

    return this.nodes()
      .filter(node => !filter.categoryIds.length || filter.categoryIds.includes(node.id))
      .map(node => ({
        ...node,
        children: node.children
          .map(child => ({ ...child, items: this.sortItems(child.items.filter(item => this.matchesFilter(item, search, filter))) }))
          .filter(child => child.items.length > 0),
      }))
      .filter(node => node.children.length > 0);
  });

  public visibleItems: Signal<OrderHistoryItemModel[]> = computed(() => this.visibleNodes()
    .flatMap(node => node.children.flatMap(child => child.items)));

  public itemPositions: Signal<Map<OrderHistoryItemModel, number>> = computed(() =>
    new Map(this.visibleItems().map((item, index) => [item, index])));

  public selectedItemIds: WritableSignal<number[]> = signal([]);

  public selectableItems: Signal<OrderHistoryItemModel[]> = computed(() => this.visibleItems()
    .filter(item => item.productData !== undefined && item.amountDelivered > 0));

  public selectedItems: Signal<OrderHistoryItemModel[]> = computed(() =>
    this.selectableItems().filter(item => this.selectedItemIds().includes(item.id)));

  public allSelectableSelected: Signal<boolean> = computed(() =>
    this.selectableItems().length > 0 && this.selectedItems().length === this.selectableItems().length);

  public itemQuantities: WritableSignal<Map<number, number>> = signal(new Map<number, number>());

  public isMobile: Signal<boolean> = toSignal(inject(ViewportService).isMobile(), { initialValue: false });

  public selectionVisible: Signal<boolean> = computed(() => this.canReorder()
    && this.selectableItems().length > 0
    && (!this.isMobile() || this.selectedItems().length > 0));

  protected readonly trackingAvailability: typeof TRACKING_AVAILABILITY = TRACKING_AVAILABILITY;

  private readonly selectionActions: Signal<TemplateRef<unknown> | undefined> = viewChild('selectionActions', { read: TemplateRef<unknown> });
  private readonly bottomSheet: MatBottomSheet = inject(MatBottomSheet);
  private readonly scrollStrategyOptions: ScrollStrategyOptions = inject(ScrollStrategyOptions);
  private readonly orderHistoryService: OrderHistoryService = inject(OrderHistoryService);
  private readonly categoryNodeService: CategoryNodeService = inject(CategoryNodeService);
  private readonly cartTracking: CartTrackingService = inject(CartTrackingService);

  private selectionSheet: MatBottomSheetRef<unknown> | undefined;

  private readonly selectionActionsEffect: EffectRef = effect(() => {
    const selectionActions: TemplateRef<unknown> | undefined = this.selectionActions();

    if (this.selectionVisible() && selectionActions) {
      this.openSelectionActions(selectionActions);
    } else {
      this.closeSelectionActions();
    }
  });

  private readonly stopSelectionSheetOnDestroy: () => void = inject(DestroyRef).onDestroy(() => void this.closeSelectionActions());

  public onItemQuantityChanged(item: OrderHistoryItemModel, quantity: number): void {
    this.itemQuantities.update((quantities: Map<number, number>) => new Map(quantities).set(item.id, quantity));
  }

  public clearSelection(): void {
    this.selectedItemIds.set([]);
  }

  public addSelectedToCart(): void {
    const order: OrderHistoryRecordModel | undefined = this.order();
    const items: OrderHistoryItemModel[] = this.selectedItems();

    if (!order || !items.length) {
      return;
    }

    const quantities: Map<number, number> = this.itemQuantities();
    const selection: OrderHistoryCartItemModel[] = items.map((item: OrderHistoryItemModel) => ({
      item,
      quantity: quantities.get(item.id) ?? item.amountOrdered,
    }));

    void this.orderHistoryService.addItemsToCart(selection).then(result => {
      const trackedItems: OrderHistoryItemModel[] = selection.map(({ item, quantity }: OrderHistoryCartItemModel) => ({
        ...item,
        amountOrdered: quantity,
      }));

      this.cartTracking.trackAddToCartOrder({ ...order, items: trackedItems }, result.errors);
      this.clearSelection();
    });
  }

  public isSelected(item: OrderHistoryItemModel): boolean {
    return this.selectedItemIds().includes(item.id);
  }

  public selectAllAvailable(selectAll: boolean): void {
    this.selectedItemIds.set(selectAll ? this.selectableItems().map(item => item.id) : []);
  }

  public itemSelectionChanged(item: OrderHistoryItemModel, selected: boolean): void {
    this.selectedItemIds.update((ids: number[]) => selected
      ? [...ids, item.id]
      : ids.filter((id: number) => id !== item.id));
  }

  private matchesFilter(item: OrderHistoryItemModel, search: string, filter: OrderHistoryRecordFilter): boolean {
    return this.matchesSearch(item, search)
      && this.matchesAvailability(item, filter.availability)
      && this.matchesDeliveryDate(item, filter.delivery);
  }

  private matchesSearch(item: OrderHistoryItemModel, search: string): boolean {
    if (!search) {
      return true;
    }

    return this.itemName(item).toLowerCase().includes(search) || item.sku.toLowerCase().includes(search);
  }

  private matchesAvailability(item: OrderHistoryItemModel, availability: OrderHistoryItemAvailabilityType | undefined): boolean {
    if (!availability) {
      return true;
    }

    if (item.amountDelivered === 0) {
      return availability === 'unavailable';
    }

    return item.amountDelivered < item.amountOrdered ? availability === 'limited' : availability === 'available';
  }

  private matchesDeliveryDate(item: OrderHistoryItemModel, delivery: OrderHistoryRecordFilter['delivery']): boolean {
    if (!delivery?.from && !delivery?.to) {
      return true;
    }

    if (!item.deliveryAt) {
      return false;
    }

    const deliveredAt: DateTime = DateTime.fromISO(item.deliveryAt);

    return (!delivery.from || deliveredAt >= delivery.from.startOf('day'))
      && (!delivery.to || deliveredAt <= delivery.to.endOf('day'));
  }

  private sortItems(items: OrderHistoryItemModel[]): OrderHistoryItemModel[] {
    const sort: OrderHistoryItemSortByValueType = this.sort();

    if (sort === 'position') {
      return items;
    }

    const direction: number = sort === 'name' ? 1 : -1;

    return [...items].sort((a, b) => direction * this.itemName(a).localeCompare(this.itemName(b)));
  }

  private itemName(item: OrderHistoryItemModel): string {
    return item.productData?.details?.name ?? item.name;
  }

  private openSelectionActions(selectionActions: TemplateRef<unknown>): void {
    this.selectionSheet = this.bottomSheet.open(selectionActions, {
      hasBackdrop: false,
      closeOnNavigation: true,
      scrollStrategy: this.scrollStrategyOptions.reposition(),
      panelClass: ['order-history-selection-overlay'],
    });
  }

  private closeSelectionActions(): void {
    this.selectionSheet?.dismiss();
    this.selectionSheet = undefined;
  }
}
